import { ExtractPayloadType } from "@hyper-fetch/core";
import { useSubmit } from "@hyper-fetch/react";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import { API_BASE_URL, client } from "@/api/client";
import { sdk } from "@/api/sdk";
import { resetPosthogUser } from "@/features/analytics/modules/posthog/posthog";
import type { LegalConsentKind } from "@complete-athlete/enums";

export type OAuthIntent = "signup" | "login";

export const useAuth = () => {
  const navigate = useNavigate();
  const loginResponse = useSubmit(sdk.auth.login.$post);
  const signupResponse = useSubmit(sdk.auth.signup.$post);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);

  const signIn = useCallback(
    async (payload: ExtractPayloadType<typeof sdk.auth.login.$post>) => {
      return loginResponse.submit({ payload });
    },
    [loginResponse],
  );

  const signUp = useCallback(
    async (payload: ExtractPayloadType<typeof sdk.auth.signup.$post>) => {
      return signupResponse.submit({ payload });
    },
    [signupResponse],
  );

  const signInWithGoogle = useCallback((intent: OAuthIntent = "login", consents: LegalConsentKind[] = [], birthDate = "") => {
    // Top-level navigation (not a cross-origin fetch) so Better Auth's OAuth
    // state/PKCE cookie is set first-party on the backend domain. A fetch here
    // sets it as a third-party cookie that browsers drop when the frontend and
    // backend live on different domains, causing `state_mismatch` on callback.
    setGoogleSubmitting(true);
    window.location.href = `${API_BASE_URL}/auth/google/${intent}?consents=${consents.join(",")}&birthDate=${birthDate}`;
  }, []);

  /** Clears what this browser knows about the person, for when the session has already ended. */
  const forgetSession = useCallback(async () => {
    resetPosthogUser();
    const meCacheKey = sdk.users.me.$get.cacheKey;
    client.cache.delete(meCacheKey);
    client.cache.invalidate(/\/users\/me/);
    await client.cache.clear();
    navigate({ to: "/login" });
  }, [navigate]);

  const signOut = useCallback(async () => {
    await sdk.auth.logout.$get.send();
    await forgetSession();
  }, [forgetSession]);

  return {
    signIn,
    signUp,
    signOut,
    forgetSession,
    loginResponse,
    signupResponse,
    signInWithGoogle,
    googleSubmitting,
  };
};
